/**
 * MemberGate — wraps public flows (booking, membership) so only signed-in
 * hub members with a WhatsApp number attached can proceed.
 *
 * States:
 *   1. Not signed in → show Google Sign-In pitch
 *   2. Signed in but no phone → show WhatsApp phone capture form
 *   3. Signed in + phone verified → render children with a small member badge
 *
 * Placement is intentional — we don't hard-block guest bookings on the
 * backend so admin/desk-manager flows keep working. The gate only enforces
 * the requirement for public users on the marketing site.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useHubMember } from "@/lib/HubMemberContext";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { CheckCircle2, Phone, ShieldCheck, LogOut, MessageCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MemberGate({ children, purpose = "book a slot", returnTo }) {
  const { ready, member, logout, refresh } = useHubMember();

  if (!ready) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-white/50 text-sm">
        Loading…
      </div>
    );
  }

  // Step 1: Not signed in
  if (!member) {
    return (
      <div className="max-w-lg mx-auto py-16 px-6" data-testid="member-gate-signin">
        <div className="border border-brand-orange/50 bg-[#131A2B] p-8">
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">
            // Members-only · HTP Sports Hub
          </div>
          <div className="font-display text-2xl md:text-3xl font-black uppercase mt-2">
            Create your Hub Member ID to {purpose}
          </div>
          <div className="text-sm text-white/60 mt-3">
            Every booking or membership at HTP Sports Hub is linked to a unique
            <span className="brand-blue"> Hub-ID</span> — that&apos;s how we save your history,
            send WhatsApp reminders, and remember your favourite courts. One-tap sign in
            with Google, then add your WhatsApp number, and you&apos;re set.
          </div>
          <ul className="mt-5 space-y-2 text-xs text-white/70">
            <li className="flex gap-2"><CheckCircle2 size={14} className="text-green-400 shrink-0 mt-0.5" /> No passwords — one-tap Google login</li>
            <li className="flex gap-2"><CheckCircle2 size={14} className="text-green-400 shrink-0 mt-0.5" /> Booking QR + reminders straight on WhatsApp</li>
            <li className="flex gap-2"><CheckCircle2 size={14} className="text-green-400 shrink-0 mt-0.5" /> Membership benefits + priority slot pick</li>
          </ul>
          <div className="mt-6 flex justify-center">
            <GoogleSignInButton returnTo={returnTo || window.location.pathname} label="Continue with Google" size="lg" testId="gate-google-btn" />
          </div>
          <div className="mt-4 text-center text-[10px] font-mono text-white/40 uppercase tracking-widest">
            we never post to your Google account
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Signed in but no phone
  if (!member.phone_verified) {
    return <PhoneOtpStep member={member} logout={logout} onDone={refresh} />;
  }

  // Step 3: Fully onboarded
  return (
    <div>
      <div className="border border-green-500/30 bg-green-950/20 px-4 py-2 flex flex-wrap items-center gap-3 mb-4" data-testid="member-badge">
        <ShieldCheck size={16} className="text-green-400" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-green-400">Hub Member Verified</div>
          <div className="text-xs text-white/80">
            <span className="brand-blue font-mono">{member.hub_id}</span> · {member.name} · +91 {member.phone}
            {member.total_bookings > 0 && <span className="text-white/50"> · {member.total_bookings} booking{member.total_bookings === 1 ? "" : "s"}</span>}
          </div>
        </div>
        <button onClick={logout} className="text-[10px] font-mono uppercase tracking-widest text-white/50 hover:text-white/80 inline-flex items-center gap-1">
          <LogOut size={12} /> Sign out
        </button>
      </div>
      {children}
    </div>
  );
}

function PhoneOtpStep({ member, logout, onDone }) {
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [optIn, setOptIn] = useState(true);
  const [busy, setBusy] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  React.useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  const requestOtp = async (isResend = false) => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) { toast.error("Enter a 10-digit mobile number"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/auth/google/request-otp`, { phone: digits }, { withCredentials: true });
      setOtpSent(true);
      setCountdown(30);
      toast.success(isResend ? "OTP re-sent on WhatsApp" : `OTP sent on WhatsApp to +91 ${digits.slice(-10)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send OTP");
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    const code = otp.replace(/\D/g, "");
    if (code.length !== 6) { toast.error("Enter the 6-digit OTP"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/auth/google/set-phone`,
        { phone: digits, otp: code, marketing_opt_in: optIn },
        { withCredentials: true });
      toast.success(`Verified · Hub-ID ${r.data.hub_id}`);
      onDone();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "OTP verification failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-lg mx-auto py-16 px-6" data-testid="member-gate-phone">
      <div className="border border-brand-blue/40 bg-[#131A2B] p-8">
        <div className="font-mono text-[10px] uppercase tracking-widest brand-blue">// One more step</div>
        <div className="font-display text-2xl md:text-3xl font-black uppercase mt-2">
          Verify your WhatsApp number
        </div>
        <div className="text-sm text-white/60 mt-3">
          Hi <span className="brand-orange font-bold">{member.name}</span> — your Hub ID is
          <span className="brand-blue font-mono"> {member.hub_id}</span>. We&apos;ll send a
          6-digit code on WhatsApp to make sure the number is yours.
        </div>

        <form onSubmit={verify} className="mt-6 space-y-4">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1">WhatsApp mobile number</label>
            <div className="flex items-center gap-2 border border-white/15 bg-black/40 px-3 py-2">
              <span className="text-white/50 text-sm">+91</span>
              <input value={phone} onChange={(e) => setPhone(e.target.value)}
                data-testid="gate-phone-input" placeholder="9876543210" inputMode="numeric" maxLength="12"
                disabled={otpSent}
                className="flex-1 bg-transparent outline-none text-sm disabled:opacity-60" />
              <Phone size={14} className="text-white/40" />
            </div>
          </div>

          {!otpSent && (
            <button type="button" onClick={() => requestOtp(false)} disabled={busy}
              data-testid="gate-send-otp"
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-orange text-black py-3 font-bold text-xs uppercase tracking-widest disabled:opacity-50">
              <MessageCircle size={14} /> {busy ? "Sending…" : "Send OTP on WhatsApp"}
            </button>
          )}

          {otpSent && (
            <>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1">Enter 6-digit OTP</label>
                <input value={otp} onChange={(e) => setOtp(e.target.value)} required
                  data-testid="gate-otp-input" placeholder="123456" inputMode="numeric" maxLength="6"
                  autoFocus
                  className="w-full bg-black/40 border border-white/15 px-3 py-3 text-center text-lg font-mono tracking-[0.5em]" />
                <div className="mt-1 flex justify-between items-center text-[10px] font-mono text-white/40">
                  <span>Sent to +91 {phone.replace(/\D/g, "").slice(-10)}</span>
                  {countdown > 0 ? (
                    <span>Resend in {countdown}s</span>
                  ) : (
                    <button type="button" onClick={() => requestOtp(true)} className="text-brand-orange hover:underline" data-testid="gate-resend-otp">Resend OTP</button>
                  )}
                </div>
              </div>
              <label className="flex items-start gap-2 text-[11px] text-white/60">
                <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)}
                  data-testid="gate-optin" className="mt-0.5 accent-brand-orange" />
                I&apos;m OK with receiving booking updates, membership renewals, and offers on WhatsApp.
              </label>
              <button type="submit" disabled={busy} data-testid="gate-verify-otp"
                className="w-full inline-flex items-center justify-center gap-2 bg-brand-orange text-black py-3 font-bold text-xs uppercase tracking-widest disabled:opacity-50">
                <CheckCircle2 size={14} /> {busy ? "Verifying…" : "Verify & Continue"}
              </button>
              <button type="button" onClick={() => { setOtpSent(false); setOtp(""); }}
                className="w-full text-[10px] font-mono uppercase tracking-widest text-white/40 hover:text-white/70">
                Change number
              </button>
            </>
          )}

          <button type="button" onClick={logout} className="w-full text-[10px] font-mono uppercase tracking-widest text-white/40 hover:text-white/70">
            Not you? Sign out
          </button>
        </form>
      </div>
    </div>
  );
}

